import { Plugin } from 'vue';
import mixpanel, { Mixpanel } from 'mixpanel-browser';
import { debounce, type DebouncedFunc } from 'lodash-es';
import store from '@/store';
import {
  SourceEvent,
  TrackingEventType,
  UserEvent,
  LicenseEvent,
  SuperProperty,
  UserProperty,
  ProjectEvent,
  QuotationEvent,
  TrackingProperty,
  CsvSurveyImportEvent,
  eventCategoryMap,
  EventObject,
  AllEvents,
} from '@/tracking/trackingTypes';
import { Lease, LeaseState } from '@common/license/types';
import i18n from '@/i18n';
import { addBreadcrumb } from '@sentry/vue';

const API_HOST = 'api_host';

interface User {
  email: string;
  firstname: string;
  lastname: string;
  confirmedAt: string;
  auth0Id: string;
}

type TrackingPluginOptions = {
  mixpanelProjectToken: string;
  trackSentry: boolean;
};

/**
 * Convert event type to tracking function paramters
 */
type TrackingFnArgs<T> = T extends EventObject ? [T['name'], T['params']] : [T];

export class Tracker {
  mixpanel: Mixpanel;
  userAuth0Id: string;
  trackSentry: boolean = false;
  private debouncedTrackDemoProjectOpened: DebouncedFunc<() => void>;

  constructor(options: TrackingPluginOptions) {
    if (!options.mixpanelProjectToken) {
      throw new Error('mixpanelProjectToken is required to initialize mixpanel');
    }

    // mixpanel tracking consent is handled via statistics cookie consent
    // anonymous tracking is not allowed
    store.dispatch('tracking/requestInitialConsent').then((consent) => {
      if (consent.statistics && options.mixpanelProjectToken) {
        this.mixpanel = mixpanel.init(
          options.mixpanelProjectToken,
          { [API_HOST]: 'https://api-eu.mixpanel.com' },
          'default'
        );
      }
    });

    this.trackSentry = options.trackSentry;
    this.debouncedTrackDemoProjectOpened = debounce(
      () => {
        this.track(ProjectEvent.DemoProjectOpened);
      },
      2000,
      { leading: true }
    );
  }

  private identify(id?: string) {
    this.mixpanel?.identify(id);
  }

  /**
   * Resets the instance and destroys it
   * To be called when the user revokes the statistics consent
   * Existing user data is not destroyed
   */
  public destroy() {
    this.reset();

    this.mixpanel = null;
  }

  private reset() {
    this.mixpanel?.reset();
  }

  private peopleSet(properties: object) {
    if (this.mixpanel) {
      this.mixpanel.people.set(properties);
    }
  }

  /**
   * Track an event with optional parameters
   *
   * Possible events should be defined in the AllEvents type
   */
  track(...[event, properties]: TrackingFnArgs<AllEvents>): void {
    if (this.mixpanel) {
      this.mixpanel.track(event, properties);
    }

    if (this.trackSentry) {
      // Sentry events are tracked without properties to avoid sharing personal information

      const category = eventCategoryMap.get(event) || 'ui';

      addBreadcrumb({
        category,
        message: event,
        level: 'info',
      });
    }
  }

  trackIncrement(event: TrackingEventType, increment: number) {
    if (this.mixpanel) {
      this.mixpanel.people.increment(event, increment);
      this.identify(this.userAuth0Id);
    }
  }

  trackLinks(selector: string, eventName: string, properties: object = {}) {
    // link to track must be rendered before `track_links` can bind correctly
    // return value indicates whether binding was successful (selector correct, link rendered)
    this.mixpanel?.track_links(selector, eventName, {
      referrer: document.referrer,
      ...properties,
    });
  }

  register(superProperties: object) {
    this.mixpanel?.register(superProperties);
  }

  trackUserLoggedIn(user: User) {
    this.userAuth0Id = user.auth0Id;

    this.register({
      [SuperProperty.Language]: i18n.global.locale,
    });

    this.peopleSet({
      [UserProperty.Email]: user.email,
      [UserProperty.FirstName]: user.firstname,
      [UserProperty.LastName]: user.lastname,
      [UserProperty.RegistrationDate]: user.confirmedAt,
    });

    // call identify after people.set (https://developer.mixpanel.com/docs/javascript#setting-profile-properties)
    this.identify(user.auth0Id);

    this.track(UserEvent.UserLoggedIn);
  }

  trackUserLoggedOut() {
    this.track(UserEvent.UserLoggedOut);
    this.reset();
  }

  trackDemoProjectOpened() {
    this.debouncedTrackDemoProjectOpened();
  }

  trackQuotationCreated(hasCodes: boolean, hasComment: boolean, sourceComponent: string) {
    this.track(QuotationEvent.QuotationCreated, {
      [TrackingProperty.HasCodes]: Boolean(hasCodes),
      [TrackingProperty.HasComment]: Boolean(hasComment),
      [TrackingProperty.SourceComponent]: sourceComponent,
    });
  }

  trackFileAddEvent(type: '.docx' | '.pdf' | '.csv-survey', keepFiles: boolean) {
    if (type === '.docx') {
      this.track(SourceEvent.ImportedWordText);
    } else if (type === '.pdf') {
      if (keepFiles) {
        this.track(SourceEvent.ImportedPdfOriginal);
      } else {
        this.track(SourceEvent.ImportedPdfText);
      }
    } else if (type === '.csv-survey') {
      this.track(CsvSurveyImportEvent.Selected);
    }
  }

  trackUpdateLeaseState({ leaseState, lease }: { leaseState: LeaseState; lease: Lease }) {
    const properties = { [SuperProperty.LicenseType]: lease?.license?.license_type };
    this.register(properties);

    this.track(LicenseEvent.LeaseStateUpdated, {
      [TrackingProperty.LeaseState]: leaseState,
    });
  }
}

// eslint-disable-next-line @typescript-eslint/init-declarations
let trackerInstance: Tracker;

export const getTracker = (options?: TrackingPluginOptions) => {
  if (!trackerInstance) {
    trackerInstance = new Tracker(options);
  }

  return trackerInstance;
};

export const trackingPlugin: Plugin = {
  install(app, options: TrackingPluginOptions) {
    const tracker = getTracker(options);

    // options API
    app.config.globalProperties.$tracker = tracker;

    // composition API
    app.provide('$tracker', tracker);
  },
};
